import {AfterViewInit, Component, forwardRef, Inject, OnInit, ViewChild} from '@angular/core';
import Keycloak, {KeycloakProfile} from "keycloak-js";
import * as KeycloakAngular from "keycloak-angular";
import {KeycloakEventType} from "keycloak-angular";
import {KeycloakSupportService} from "@app/core/keycloak/keycloak-support.service";
import {AppComponent} from "@app/app.component";
import {KeycloakOptions} from "keycloak-angular/lib/core/interfaces/keycloak-options";
import {environment} from "@env/environment";
import {UserService} from "@app/core";
import {CountdownComponent, CountdownConfig} from "ngx-countdown";

@Component({
  selector: 'app-keycloak-info',
  templateUrl: './keycloak-info.component.html',
  styleUrls: ['./keycloak-info.component.css']
})
export class KeycloakInfoComponent implements OnInit, AfterViewInit {

  kcUserProfile: KeycloakProfile;
  kcInstance: Keycloak.KeycloakInstance;
  statusMsg: string;
  tokenExpireIn: number;
  keycloakOptions: KeycloakOptions;
  inAwClientRole: boolean;
  inAwClientRoleFromInstance: boolean;
  testRealm: string;

  ssoIdleCountdownConfig: CountdownConfig = {
    demand: true,
    leftTime: KeycloakSupportService.SsoIdleTimeout - ((new Date().getTime() - KeycloakSupportService.ssoIdleStart.getTime()) / 1000), format: 'HH:mm:ss',
    prettyText: (text) => this.keycloakSupport.prettyText(text)
  };
  ssoMaxCountdownConfig: CountdownConfig = {
    demand: true,
    leftTime: KeycloakSupportService.SsoMaxTimeout - ((new Date().getTime() - KeycloakSupportService.ssoMaxStart.getTime()) / 1000), format: 'HH:mm:ss',
    prettyText: (text) => this.keycloakSupport.prettyText(text)
  };
  accessTokenCountdownConfig: CountdownConfig = {
    demand: true,
    leftTime: this.keycloakAngular.getKeycloakInstance().tokenParsed.exp - new Date().getTime() / 1000 + (KeycloakSupportService.accessTokenStart.getTime() / 1000 - this.keycloakAngular.getKeycloakInstance().tokenParsed.iat),
    format: 'mm:ss',
    prettyText: (text) => this.keycloakSupport.prettyText(text)
  };

  @ViewChild('ssoIdleCountdown', {static: false}) private ssoIdleCountdown: CountdownComponent;
  @ViewChild('ssoMaxCountdown', {static: false}) private ssoMaxCountdown: CountdownComponent;
  @ViewChild('accessTokenCountdown', {static: false}) private accessTokenCountdown: CountdownComponent;

  constructor(@Inject(forwardRef(() => AppComponent)) public app: AppComponent,
              public user: UserService,
              private keycloakAngular: KeycloakAngular.KeycloakService,
              public keycloakSupport: KeycloakSupportService) {

    setInterval(() => {
      this.onClickCalculateTokenExpiration();
    }, 1000);

    keycloakAngular.keycloakEvents$.subscribe({
      next: (e) => {
        const indexOfEventType = Object.values(KeycloakEventType).indexOf(e.type);
        console.log("keycloak-info: keycloakEvent:", Object.keys(KeycloakEventType)[indexOfEventType], e.args);

        switch (e.type) {
          case KeycloakEventType.OnAuthRefreshSuccess:
            this.restartCountdown(this.ssoIdleCountdown);
            this.restartCountdown(this.accessTokenCountdown);
            break;
        }
      }
    });
  }

  ngOnInit(): void {
    void this.onClickLoadProfile();
    this.onClickInRole();
  }

  ngAfterViewInit() {
    this.ssoIdleCountdown.begin();
    this.ssoMaxCountdown.begin();
    this.accessTokenCountdown.begin();
  }

  onClickLogout() {
    this.user.logout();
  }

  onClickCalculateTokenExpiration() {
    if (this.kcInstance && this.kcInstance.tokenParsed && this.tokenExpireIn > 0) {
      this.tokenExpireIn = Math.round(this.kcInstance.tokenParsed.exp - new Date().getTime() / 1000);
    }
  }

  onClickInitialLoginOrReload() {
    this.user.resetUser();
    this.keycloakSupport.handleInitialLoginOrReload();
  }

  onClickReloadOptionsAndInit() {
    console.log("loading keycloak options...");
    this.keycloakOptions = this.keycloakSupport.getKeycloakOptions();
    if (this.keycloakOptions.config && this.keycloakOptions.initOptions) {
      console.log("keycloakAngularService.init() using saved options...");
      console.log(this.keycloakOptions);
      void this.keycloakAngular.init(this.keycloakOptions).then(() => {
        console.log("keycloakAngularService.init() completed...");
      });
    } else {
      console.warn("keycloak config and init options are not present...");
    }
  }

  onClickResetOptionsAndInit() {
    console.log("reset keycloak options and init...");
    this.keycloakSupport.resetKeycloakOptions(this.testRealm);
    this.keycloakOptions = this.keycloakSupport.getKeycloakOptions();
    void this.keycloakAngular.init(this.keycloakOptions).then(() => {
      console.log("keycloakAngularService.init() completed...");
    });
  }

  onClickResetAndAutoLogin() {
    this.keycloakSupport.resetKeycloakOptions(this.testRealm);
    this.keycloakOptions = this.keycloakSupport.getKeycloakOptions();
    this.onClickAutoLogin();
  }

  onClickAutoLogin(noKeycloakInit?: boolean) {
    if (environment.keycloakConfig.autoLogin) {
      console.log("invoking keycloakSupportService.autoLogin()...");
      this.keycloakOptions = this.keycloakSupport.getKeycloakOptions();
      this.keycloakSupport.autoLogin(this.keycloakOptions, noKeycloakInit);
    } else {
      console.log("auto login not enabled...");
    }
  }

  onClickLogKeycloakOptions() {
    console.log(this.keycloakOptions);
  }

  async onClickLoadProfile() {
    console.log("onClickLoadProfile() called...");
    this.kcInstance = this.keycloakAngular.getKeycloakInstance();
    this.kcUserProfile = await this.keycloakAngular.loadUserProfile();
  }

  async onClickLoadInstanceProfile() {
    console.log("onClickLoadInstanceProfile() called...");
    this.kcInstance = this.keycloakAngular.getKeycloakInstance();
    this.kcUserProfile = await this.kcInstance.loadUserProfile();
  }

  onClickInRole() {
    console.log("onClickInRole() called...");
    this.inAwClientRole = this.keycloakAngular.isUserInRole("aw-client-role");
    let kcInstance = this.keycloakAngular.getKeycloakInstance();
    this.inAwClientRoleFromInstance = kcInstance.hasRealmRole("aw-client-role");
  }

  onClickValidateRealm(realm: string) {
    this.keycloakSupport.isValidRealm(realm).subscribe(valid => {
      if (valid) {
        this.statusMsg = `${realm} is valid`;
      } else {
        this.statusMsg = `${realm} is not valid`;
      }
    });
  }

  private restartCountdown(countdown: CountdownComponent) {
    if (countdown) {
      countdown.restart();
      countdown.resume();
    }
  }
}
